import { UsersIcon } from "@heroicons/react/24/outline";

import Modal from "../../layouts/Modal";
import { IContactLeadSummary } from "../../models/Contact";
import { ButtonInput } from "../shared/Inputs/ButtonInput";
import SharedContactLeadsTable from "./SharedContactLeadsTable";

/**
 * Shown before saving a contact that more than one lead points at. "Change all" keeps the shared
 * contact and rewrites every lead's details; "Save as new contact" gives this lead its own copy
 * so the others keep the details they have now.
 */
export default function SharedContactModal({
  open,
  setOpen,
  otherLeads,
  onChangeAll,
  onSaveAsNew,
  isLoading = false,
}: {
  open: boolean;
  setOpen: (open: boolean) => void;
  otherLeads: IContactLeadSummary[];
  onChangeAll: () => void;
  onSaveAsNew: () => void;
  isLoading?: boolean;
}) {
  return (
    <Modal
      open={open}
      // Modal hands back a setState action; the callers here only ever care about the flag.
      setOpen={(value: boolean | ((prev: boolean) => boolean)) =>
        setOpen(typeof value === "function" ? value(open) : value)
      }
      width="max-w-3xl"
    >
      <div className="sm:flex sm:items-start">
        <div className="mx-auto flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-full bg-yellow-100 sm:mx-0 sm:h-10 sm:w-10">
          <UsersIcon className="h-6 w-6 text-yellow-600" aria-hidden="true" />
        </div>
        <div className="mt-3 min-w-0 text-center sm:ml-4 sm:mt-0 sm:text-left">
          <h3 className="text-lg font-medium leading-6 text-gray-900">
            This contact is shared with other leads
          </h3>
          <p className="mt-2 text-sm text-gray-500">
            Saving these details will also change the contact on the{" "}
            {otherLeads.length} other lead{otherLeads.length === 1 ? "" : "s"} below. Save as a new
            contact to change this lead only and leave the others as they are.
          </p>
        </div>
      </div>

      <div className="mt-4">
        <SharedContactLeadsTable leads={otherLeads} variant="table" />
      </div>

      <div className="mt-5 flex flex-col gap-3 sm:mt-4 sm:flex-row sm:justify-end">
        <ButtonInput
          label="Cancel"
          onClick={() => setOpen(false)}
          disabled={isLoading}
          classes="border border-gray-300 !bg-white !text-gray-700 hover:!bg-gray-50"
        />
        <ButtonInput
          label="Yes, change all"
          onClick={onChangeAll}
          isLoading={isLoading}
          disabled={isLoading}
          classes="!bg-red-600 hover:!bg-red-700"
        />
        <ButtonInput
          label="Save as new contact"
          onClick={onSaveAsNew}
          isLoading={isLoading}
          disabled={isLoading}
          classes=""
        />
      </div>
    </Modal>
  );
}
